import React from "react";
import { useGetLoanRequestDetailsQuery } from "../../../../store/admin/loan";
import { formatMoney } from "../../../../utils";
import moment from "moment";
import { CardComp } from "../../../../components";
import FallbackImage from "../../../../components/Common/ImageWrapper";
import { convertToTitleCase } from "../../../../utils/helpers";

const LoanDetailsModal = ({ loanId, handleClose }) => {
  const {
    data: loanDetails,
    isLoading,
    isError,
  } = useGetLoanRequestDetailsQuery(loanId, {
    skip: !loanId,
  });

  const loan = loanDetails || {};

  const getStaffDisplayName = (staff, fallback = "N/A") => {
    if (!staff) return fallback;
    const name = [staff?.first_name, staff?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (name) return name;
    if (staff?.name) return staff.name;
    if (staff?.email) return staff.email;
    return fallback;
  };

  const getDateLabel = (value) =>
    value ? moment(value).format("DD-MMM-YYYY HH:mm") : "N/A";

  const actionTrail = [
    {
      label: "Recorded by",
      user: loan?.recorded_by || loan?.recordedBy,
      timestamp: loan?.created_at,
      fallback: "Member",
      show: true,
    },
    {
      label: "Approved by",
      user: loan?.approved_by || loan?.approvedBy,
      timestamp: loan?.approved_at || loan?.approvedAt,
      fallback: "Pending approval",
      show: !!(loan?.approved_by || loan?.approved_at || loan?.approvedBy),
    },
    {
      label: "Disbursed by",
      user: loan?.disbursed_by || loan?.disbursedBy,
      timestamp: loan?.disbursed_at || loan?.disbursedAt,
      fallback: "Not disbursed",
      show: !!(loan?.disbursed_by || loan?.disbursed_at || loan?.disbursedBy),
    },
    {
      label: "Declined by",
      user: loan?.declined_by || loan?.declinedBy,
      timestamp: loan?.declined_at || loan?.declinedAt,
      fallback: loan?.decline_reason ? "Declined" : "N/A",
      show:
        loan?.status?.toUpperCase() === "DECLINED" ||
        !!(loan?.declined_by || loan?.declined_at || loan?.declinedBy),
    },
  ];

  if (isLoading) {
    return (
      <div className="py-4">
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>

        {/* Action Trail */}
        <div className="col-12">
          <CardComp cardClassName="border-0 shadow-sm">
            <h5 className="fw-bold mb-3 text-primary1">Action Trail</h5>
            <div className="row g-3">
              {actionTrail
                .filter((item) => item.show)
                .map((item) => (
                  <div className="col-md-6" key={item.label}>
                    <div className="mb-3">
                      <small className="text-muted d-block mb-1">
                        {item.label}
                      </small>
                      <span className="fw-bold">
                        {getStaffDisplayName(item.user, item.fallback)}
                      </span>
                    </div>
                    <div>
                      <small className="text-muted d-block mb-1">Timestamp</small>
                      <span>{getDateLabel(item.timestamp)}</span>
                    </div>
                  </div>
                ))}
            </div>
          </CardComp>
        </div>
        </div>
      </div>
    );
  }

  if (isError || !loan?.id) {
    return (
      <div className="py-4">
        <div className="text-center py-5">
          <p className="text-danger">Failed to load loan details</p>
          {isError && (
            <small className="text-muted">
              Error: {isError?.data?.message || isError?.message || "Unknown error"}
            </small>
          )}
        </div>
      </div>
    );
  }

  const statusColors = {
    PENDING: "#FFA500",
    APPROVED: "#19BC31",
    DISBURSED: "var(--color-secondary)",
    DECLINED: "#FF4343",
    PAID: "#19BC31",
  };
  const statusColor = statusColors[loan?.status?.toUpperCase()] || "#A1A0A0";
  const getStatusBadgeColor = (status) =>
    statusColors[status?.toUpperCase()] || "#A1A0A0";

  return (
    <div className="py-3">
      <div className="text-center mb-4">
        <h3 className="text-primary1 fw-bold mb-2">Loan Details</h3>
        <div
          className="border-bottom border-2 border-primary1 mx-auto"
          style={{ width: "100px" }}
        ></div>
      </div>

      <div className="row g-3">
        {/* Member Information */}
        <div className="col-12">
          <CardComp cardClassName="border-0 shadow-sm">
            <h5 className="fw-bold mb-3 text-primary1">Member Information</h5>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="d-flex align-items-center mb-2">
                  <FallbackImage
                    className="rounded-circle me-3"
                    width="50px"
                    height="50px"
                    src={loan?.member?.photo}
                  />
                  <div>
                    <h6 className="mb-0 fw-bold">
                      {loan?.member?.first_name} {loan?.member?.last_name}
                    </h6>
                    <small className="text-muted">
                      {loan?.member?.member_no ? `#${loan.member.member_no}` : "N/A"}
                    </small>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-2">
                  <small className="text-muted d-block">Email</small>
                  <span>{loan?.member?.email || "N/A"}</span>
                </div>
                <div className="mb-2">
                  <small className="text-muted d-block">Phone</small>
                  <span>{loan?.member?.phone || "N/A"}</span>
                </div>
              </div>
            </div>
          </CardComp>
        </div>

        {/* Loan Information */}
        <div className="col-12">
          <CardComp cardClassName="border-0 shadow-sm">
            <h5 className="fw-bold mb-3 text-primary1">Loan Information</h5>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Loan Type</small>
                  <span className="fw-bold">
                    {loan?.loan_type?.name || "N/A"}
                  </span>
                </div>
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Loan Amount</small>
                  <span className="fw-bold text-primary1">
                    {formatMoney(loan?.loan_amount ?? loan?.amount ?? 0, true)}
                  </span>
                </div>
                {loan?.repayment_plan && (
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">
                      Repayment Plan
                    </small>
                    <span className="fw-bold">
                      {convertToTitleCase(loan.repayment_plan)}
                    </span>
                  </div>
                )}
              </div>
              <div className="col-md-6">
                {loan?.duration && (
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Duration</small>
                    <span className="fw-bold">
                      {loan.duration}{" "}
                      {convertToTitleCase(loan?.duration_type || "")}
                    </span>
                  </div>
                )}
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Status</small>
                  <div
                    className="px-3 py-1 rounded-pill text-white d-inline-block"
                    style={{
                      backgroundColor: statusColor,
                    }}
                  >
                    {convertToTitleCase(loan?.status || "UNKNOWN")}
                  </div>
                </div>
                {loan?.reference && (
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Reference</small>
                    <span className="fw-bold">{loan.reference}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="row g-3 mt-2">
              {loan?.created_at && (
                <div className="col-md-6">
                  <small className="text-muted d-block mb-1">Created At</small>
                  <span className="fw-bold">
                    {moment(loan.created_at).format("DD-MMM-YYYY HH:mm")}
                  </span>
                </div>
              )}
              {loan?.payback_at && (
                <div className="col-md-6">
                  <small className="text-muted d-block mb-1">Payback Date</small>
                  <span className="fw-bold">
                    {moment(loan.payback_at).format("DD-MMM-YYYY")}
                  </span>
                </div>
              )}
            </div>
            {loan?.decline_reason && (
              <div className="mt-3 pt-3 border-top">
                <small className="text-muted d-block mb-1">
                  Decline Reason
                </small>
                <span className="text-danger">{loan.decline_reason}</span>
              </div>
            )}
          </CardComp>
        </div>

        {/* Guarantors */}
        {Array.isArray(loan?.guarantors) && loan.guarantors.length > 0 && (
          <div className="col-12">
            <CardComp cardClassName="border-0 shadow-sm">
              <h5 className="fw-bold mb-3 text-primary1">Guarantors</h5>
              <div className="row g-3">
                {loan.guarantors.map((guarantor, idx) => (
                  <div key={guarantor?.id || idx} className="col-12 col-md-6">
                    <div className="border rounded-4 p-3 h-100 bg-light">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <p className="text-muted small mb-1">Name</p>
                          <h6 className="fw-bold mb-0">
                            {guarantor?.name || "N/A"}
                          </h6>
                        </div>
                        {guarantor?.status && (
                          <span
                            className="px-3 py-1 rounded-pill text-white fw-semibold"
                            style={{
                              backgroundColor: getStatusBadgeColor(
                                guarantor.status
                              ),
                              fontSize: "11px",
                            }}
                          >
                            {convertToTitleCase(guarantor.status)}
                          </span>
                        )}
                      </div>
                      <div className="mb-2">
                        <small className="text-muted d-block">Phone</small>
                        <span>{guarantor?.phone || "N/A"}</span>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted d-block">Email</small>
                        <span>{guarantor?.email || "N/A"}</span>
                      </div>
                      {guarantor?.occupation && (
                        <div className="mb-2">
                          <small className="text-muted d-block">Occupation</small>
                          <span>{guarantor.occupation}</span>
                        </div>
                      )}
                      {guarantor?.home_address && (
                        <div className="mb-2">
                          <small className="text-muted d-block">Home Address</small>
                          <span>{guarantor.home_address}</span>
                        </div>
                      )}
                      {guarantor?.office_address && (
                        <div>
                          <small className="text-muted d-block">Office Address</small>
                          <span>{guarantor.office_address}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardComp>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanDetailsModal;

